import { createTheme, type HqPalette, type HqTheme, type ThemeMode } from './tokens';

/**
 * 机构差异化主题：来源后端 `sys_org_config`（DR-11.5），登录后由前端注入。
 * 读取端点 `GET /api/auth/org-config`（当前机构）/ `GET /api/sys/org-config/{orgId}`（管理员），
 * 维护端点 `PUT /api/sys/org-config/{orgId}`（门禁 `sys:config:manage`，v3.17）。
 * 字段与本契约一一对应：`appTitle` ← `portal_title`/`app_title`、`logoUrl` ← `logo_url`、
 * `primaryColor` ← `theme_primary_color`/`primary_color`、`mode` ← `theme_mode`。
 */
export interface OrgThemeConfig {
  orgId: string;
  appTitle?: string;
  logoUrl?: string;
  /** 机构主色，如 #0e7c86。 */
  primaryColor?: string;
  mode?: ThemeMode;
}

const HEX_PATTERN = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i;

export function isHexColor(value: string | null | undefined): boolean {
  return !!value && HEX_PATTERN.test(value.trim());
}

function toRgb(hex: string): [number, number, number] {
  const raw = hex.replace('#', '').trim();
  const full = raw.length === 3 ? raw.split('').map((c) => `${c}${c}`).join('') : raw;
  return [
    Number.parseInt(full.slice(0, 2), 16),
    Number.parseInt(full.slice(2, 4), 16),
    Number.parseInt(full.slice(4, 6), 16)
  ];
}

function toHex(rgb: [number, number, number]): string {
  return `#${rgb.map((v) => Math.round(Math.min(255, Math.max(0, v))).toString(16).padStart(2, '0')).join('')}`;
}

/** 与目标色按比例混合：ratio=0 返回原色，1 返回目标色。 */
export function mixHex(source: string, target: string, ratio: number): string {
  if (!isHexColor(source) || !isHexColor(target)) return source;
  const from = toRgb(source);
  const to = toRgb(target);
  const clampRatio = Math.min(1, Math.max(0, ratio));
  return toHex([
    from[0] + (to[0] - from[0]) * clampRatio,
    from[1] + (to[1] - from[1]) * clampRatio,
    from[2] + (to[2] - from[2]) * clampRatio
  ]);
}

/**
 * 按机构配置生成主题：主色派生 hover/active/浅底，
 * 保证 Web(AntD)、RN(Paper)、小程序(NutUI) 三端主色一致。
 */
export function createOrgTheme(config: Partial<OrgThemeConfig> = {}): HqTheme {
  const palette = config.primaryColor && isHexColor(config.primaryColor) ? config.primaryColor : undefined;
  if (!palette) return createTheme({ mode: config.mode });

  return createTheme({
    mode: config.mode,
    palette: {
      primary: palette,
      primaryHover: mixHex(palette, '#ffffff', 0.25),
      primaryActive: mixHex(palette, '#000000', 0.15),
      primaryBg: mixHex(palette, '#ffffff', 0.92),
      info: palette
    }
  });
}

/**
 * 读取 Vite 环境变量中的自定义配色（VITE_CUSTOM_*），返回可直接合入 palette 的覆盖项。
 * 缺失或非法的色值回退 defaultPalette，Web 站点与大屏共用同一套变量名。
 */
export function customPaletteFromEnv(env: Record<string, unknown>): Partial<HqPalette> {
  const read = (key: string): string | undefined => {
    const value = env[key];
    return typeof value === 'string' && isHexColor(value) ? value.trim() : undefined;
  };

  const primary = read('VITE_CUSTOM_PRIMARY_COLOR');
  const light = read('VITE_CUSTOM_PRIMARY_COLOR_LIGHT');
  const dark = read('VITE_CUSTOM_PRIMARY_COLOR_DARK');
  const secondary = read('VITE_CUSTOM_SECONDARY_COLOR');
  const accent = read('VITE_CUSTOM_ACCENT_COLOR');

  const palette: Partial<HqPalette> = {};
  if (primary) {
    palette.primary = primary;
    palette.info = primary;
    palette.primaryHover = light ?? mixHex(primary, '#ffffff', 0.25);
    palette.primaryActive = dark ?? mixHex(primary, '#000000', 0.15);
    palette.primaryBg = mixHex(primary, '#ffffff', 0.92);
  }
  if (secondary) palette.secondary = secondary;
  if (accent) palette.accent = accent;
  return palette;
}

/** 把主题 palette 写入 :root CSS 变量，供 global.css / 内联样式引用。 */
export function applyThemeCssVars(theme: HqTheme, prefix = 'hq'): void {
  if (typeof document === 'undefined') return;
  const vars = themeToCssVars(theme, prefix);
  for (const [name, value] of Object.entries(vars)) {
    document.documentElement.style.setProperty(name, value);
  }
}

/** 生成 CSS 变量（Web 站点注入到 :root；RN/小程序不使用）。 */
export function themeToCssVars(theme: HqTheme, prefix = 'hq'): Record<string, string> {
  const vars: Record<string, string> = {};
  for (const [key, value] of Object.entries(theme.palette)) {
    vars[`--${prefix}-color-${kebab(key)}`] = value;
  }
  for (const [key, value] of Object.entries(theme.spacing)) {
    vars[`--${prefix}-space-${kebab(key)}`] = `${value}px`;
  }
  for (const [key, value] of Object.entries(theme.radius)) {
    vars[`--${prefix}-radius-${kebab(key)}`] = `${value}px`;
  }
  for (const [key, value] of Object.entries(theme.fontSize)) {
    vars[`--${prefix}-font-${kebab(key)}`] = `${value}px`;
  }
  return vars;
}

function kebab(value: string): string {
  return value.replace(/([a-z0-9])([A-Z])/g, '$1-$2').toLowerCase();
}
